import { ConnectedPosition } from "@angular/cdk/overlay";
import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ISMultiSelectOption, ISPagination, MVListingConfigs } from "is-lib";
import * as _ from 'lodash';
import { debounceTime, distinctUntilChanged, merge } from "rxjs";
import { IContractAccessRequestResponse } from "src/app/models/ContractAccessRequest";
import { ServerResponse } from "src/app/models/ServerResponse";
import { ContractsService } from "src/app/services/contracts.service";
import { ContractAccessComponent } from "../contract-access.component";

type AccessRequestColumn = keyof IContractAccessRequestResponse;

@Component({
  selector: 'app-contracts-access-list',
  templateUrl: './contract-access-list.component.html',
  styleUrls: ['./contract-access-list.component.scss'],
})

export class ContractsAccessListComponent implements OnInit {

    connectedOverlayPositions: ConnectedPosition = {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetY: 10
    }

    listingConfig: MVListingConfigs = new MVListingConfigs([], null, new ISPagination(999, 0));
    accessRequests: IContractAccessRequestResponse[] = [];
    visibleRequests: IContractAccessRequestResponse[] = [];
    isLoading: boolean = false;
    hasError: boolean = false;
    filterOverlayIsOpen: boolean = false;

    paginationPageSizes = [ 5, 10, 25, 50, 100 ];
    form = new FormGroup({
        search: new FormControl<string | null>(null),
        valid: new FormControl<string[] | null>(null),
        permissionGranted: new FormControl<string[] | null>(null),
        pageSize: new FormControl<number>(10),
        page: new FormControl<number>(1)
    });

    yesNoOptions: ISMultiSelectOption[] = [
        new ISMultiSelectOption('true', 'YES'),
        new ISMultiSelectOption('false', 'NO')
    ];

    // Most recent requests first by default
    sortColumn: AccessRequestColumn | null = 'creationDate';
    sortDirection: 'asc' | 'desc' = 'desc';

    constructor(private _contractsService: ContractsService, private _dialog: MatDialog) { }

    ngOnInit(): void {
        merge(
            this.form.controls.search.valueChanges.pipe( debounceTime(300), distinctUntilChanged() ),
            this.form.controls.valid.valueChanges,
            this.form.controls.permissionGranted.valueChanges,
            this.form.controls.pageSize.valueChanges
        ).subscribe(() => {
            this.form.controls.page.setValue(1);
            this.applyFilters();
        });

        this.fetchAccessRequests();
    }

    fetchAccessRequests(): void {
        this.isLoading = true;
        this.hasError = false;

        this._contractsService.getContractAccessRequests(this.listingConfig, true).subscribe({
            next: (sr: ServerResponse<IContractAccessRequestResponse[]>) => {
                this.accessRequests = Array.isArray(sr?.data) ? sr.data : [];
                this.isLoading = false;
                this.applyFilters();
            },
            error: () => {
                this.accessRequests = [];
                this.hasError = true;
                this.isLoading = false;
                this.applyFilters();
            }
        });
    }

    applyFilters(): void {
        let search = this.form.controls.search.value?.trim().toLowerCase();
        let valid = this.form.controls.valid.value;
        let permissionGranted = this.form.controls.permissionGranted.value;

        let filtered = this.accessRequests.filter( (request) =>
            (!search || [request.contactEmail, request.contactNumber, request.entityTIN].some( (value) => value?.toString().toLowerCase().includes(search!) ))
            && (!valid?.length || valid.includes(String(request.isContractAccessRequestValid)))
            && (!permissionGranted?.length || permissionGranted.includes(String(request.permissionGranted)))
        );

        this.visibleRequests = this.sortColumn ? _.orderBy(filtered, this.sortColumn, this.sortDirection) : filtered;
    }

    sortBy(column: AccessRequestColumn): void {
        if (this.sortColumn != column) {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        } else if (this.sortDirection == 'asc') {
            this.sortDirection = 'desc';
        } else {
            this.sortColumn = null;
        }

        this.applyFilters();
    }

    getSortIconClass(column: AccessRequestColumn): string {
        if (this.sortColumn != column) {
            return '';
        }

        return this.sortDirection == 'asc' ? 'fa-sort-up' : 'fa-sort-down';
    }

    previousPage(): void {
        if (this.currentPage > 1) {
            this.form.controls.page.setValue(this.currentPage - 1);
        }
    }

    nextPage(): void {
        if (this.currentPage < this.maxPage) {
            this.form.controls.page.setValue(this.currentPage + 1);
        }
    }

    onContractAccessRequest(): void {
        this._dialog.open(ContractAccessComponent, {
            width: '90vw',
            maxWidth: '640px',
            maxHeight: '90vh',
            panelClass: 'responsive-dialog',
        })
        .afterClosed()
        .subscribe((refreshContractAccesses) => {
            if (refreshContractAccesses) {
                this.fetchAccessRequests();
            }
        });
    }

    get activeFilterCount(): number {
        let count = 0;

        if (this.form.controls.valid.value?.length)
            count++;
        if (this.form.controls.permissionGranted.value?.length)
            count++;

        return count;
    }

    get pageSize(): number {
        return this.form.controls.pageSize.value!;
    }

    get currentPage(): number {
        return this.form.controls.page.value!;
    }

    get maxPage(): number {
        return Math.max(1, Math.ceil(this.visibleRequests.length / this.pageSize));
    }

    get requestsPage(): IContractAccessRequestResponse[] {
        let pageStart = this.pageSize * (this.currentPage - 1);

        return this.visibleRequests.slice(pageStart, pageStart + this.pageSize);
    }

    get requestsRange() {
        let total = this.visibleRequests.length;
        let from = Math.min(this.pageSize * (this.currentPage - 1) + 1, total);
        let to = from == 0 ? 0 : from + this.requestsPage.length - 1;

        return { from, to, total };
    }
}
